// Find and filter library objects. Pure: the tests hold it, the Library panel draws it.
//
// Three things combine, and an object must pass all three:
//   1. SHOW   - all, favourites, the person's own objects, or the built-in ones.
//   2. WORDS  - the text typed. First the exact text is looked for in the names (as the panel always did).
//               If no object has it, every word must match, in any order (see makeMatcher, parseQuery).
//   3. FACETS - for pumps: series, poles, view, inlet DN, outlet DN, motor kW.
//               for pipe fittings: kind and size.
// The facet values come from the objects themselves (group meta.pump and meta.fitting), so a
// new pump or a new fitting size shows up in the filters with no change here.
import type { LibraryItem, LibrarySource } from "./library";

export type RowSource = LibrarySource | "builtin";
export interface FilterRow {
  item: LibraryItem;
  source: RowSource;
}

// ----------------------------------------------------------------------------- the facets --

export interface PumpFacets {
  series: string;
  model: string;
  view: string;
  inletDn: number;
  outletDn: number;
  kW: number | null;
  poles: number | null;
}
export interface FittingFacets {
  kind: string;
  size: number;
  size2: number | null;
}
export interface Facets {
  pump: PumpFacets | null;
  fitting: FittingFacets | null;
}

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);

function readPump(v: unknown): PumpFacets | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const series = str(o.series);
  const model = str(o.model);
  const view = str(o.view);
  const inletDn = num(o.inletDn);
  const outletDn = num(o.outletDn);
  if (!series || !model || !view || inletDn === null || outletDn === null) return null;
  return { series, model, view, inletDn, outletDn, kW: num(o.kW), poles: num(o.poles) };
}

function readFitting(v: unknown): FittingFacets | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const kind = str(o.kind);
  const size = num(o.size);
  if (!kind || size === null) return null;
  return { kind, size, size2: num(o.size2) };
}

const facetCache = new WeakMap<LibraryItem, Facets>();

/** What an object says about itself: the pump or fitting data on its outermost group. */
export function facetsOf(item: LibraryItem): Facets {
  const hit = facetCache.get(item);
  if (hit) return hit;
  const inner = new Set<string>();
  for (const e of item.entities) if (e.type === "group") for (const c of e.children) inner.add(c);
  let pump: PumpFacets | null = null;
  let fitting: FittingFacets | null = null;
  for (const e of item.entities) {
    if (inner.has(e.id) || !e.meta) continue;
    pump = readPump(e.meta.pump) ?? pump;
    fitting = readFitting(e.meta.fitting) ?? fitting;
  }
  const out = { pump, fitting };
  facetCache.set(item, out);
  return out;
}

export type RowKind = "pump" | "fitting" | "other";
export const kindOf = (item: LibraryItem): RowKind => {
  const f = facetsOf(item);
  return f.pump ? "pump" : f.fitting ? "fitting" : "other";
};

// ------------------------------------------------------------------------------- the state --

export type ShowMode = "all" | "favourites" | "mine" | "builtin";
export type FacetKey = "series" | "poles" | "view" | "inletDn" | "outletDn" | "kW" | "fittingKind" | "fittingSize";
export const FACET_KEYS: readonly FacetKey[] = ["series", "poles", "view", "inletDn", "outletDn", "kW", "fittingKind", "fittingSize"];

export interface FilterState {
  text: string;
  show: ShowMode;
  series: string | null;
  poles: number | null;
  view: string | null;
  inletDn: number | null;
  outletDn: number | null;
  kW: number | null;
  fittingKind: string | null;
  fittingSize: number | null;
}

export const NO_FILTER: FilterState = {
  text: "",
  show: "all",
  series: null,
  poles: null,
  view: null,
  inletDn: null,
  outletDn: null,
  kW: null,
  fittingKind: null,
  fittingSize: null
};

/** Is any word typed or any control set? */
export function isFiltering(s: FilterState): boolean {
  return s.text.trim() !== "" || s.show !== "all" || FACET_KEYS.some((k) => s[k] !== null);
}

/** How many controls are set (the words count as one). */
export function activeCount(s: FilterState): number {
  return (s.text.trim() !== "" ? 1 : 0) + (s.show !== "all" ? 1 : 0) + FACET_KEYS.filter((k) => s[k] !== null).length;
}

const FACET_KIND: Record<FacetKey, RowKind> = {
  series: "pump",
  poles: "pump",
  view: "pump",
  inletDn: "pump",
  outletDn: "pump",
  kW: "pump",
  fittingKind: "fitting",
  fittingSize: "fitting"
};

/** The values one object has for one facet. A tee has two sizes; a CF pump has no poles. */
export function valuesOf(f: Facets, key: FacetKey): Array<string | number> {
  const p = f.pump;
  const g = f.fitting;
  switch (key) {
    case "series":
      return p ? [p.series] : [];
    case "poles":
      return p && p.poles !== null ? [p.poles] : [];
    case "view":
      return p ? [p.view] : [];
    case "inletDn":
      return p ? [p.inletDn] : [];
    case "outletDn":
      return p ? [p.outletDn] : [];
    case "kW":
      return p && p.kW !== null ? [p.kW] : [];
    case "fittingKind":
      return g ? [g.kind] : [];
    case "fittingSize":
      return g ? (g.size2 !== null && g.size2 !== g.size ? [g.size, g.size2] : [g.size]) : [];
  }
}

// -------------------------------------------------------------------------------- the words --

export type Term =
  | { t: "text"; s: string }
  | { t: "num"; n: number }
  | { t: "kw"; n: number }
  | { t: "poles"; n: number }
  | { t: "dn"; n: number };

/**
 * Split what the person typed into terms. Every term must match (when no name has the exact text).
 *   "55 kw", "55kw"     -> a motor of 55 kW
 *   "2 pole", "4-pole"  -> a motor with 2 (or 4) poles
 *   "dn100", "DN 100"   -> a connection or fitting of size 100
 *   "125"               -> the whole number 125 anywhere (a size, a power, an impeller)
 *   "125x100", "plan"   -> those letters anywhere in the name
 * Words with no letter and no digit (the dot and the arrow in a pasted name) are ignored.
 */
export function parseQuery(q: string): Term[] {
  const terms: Term[] = [];
  let s = q.toLowerCase();
  s = s.replace(/(\d+(?:\.\d+)?)\s*kw\b/g, (_m, n: string) => {
    terms.push({ t: "kw", n: Number(n) });
    return " ";
  });
  s = s.replace(/(\d+)\s*-?\s*poles?\b/g, (_m, n: string) => {
    terms.push({ t: "poles", n: Number(n) });
    return " ";
  });
  s = s.replace(/\bdn\s*(\d+)\b/g, (_m, n: string) => {
    terms.push({ t: "dn", n: Number(n) });
    return " ";
  });
  for (const w of s.split(/\s+/)) {
    if (!w || !/[\p{L}\p{N}]/u.test(w)) continue;
    if (/^\d+(?:\.\d+)?$/.test(w)) terms.push({ t: "num", n: Number(w) });
    else terms.push({ t: "text", s: w });
  }
  return terms;
}

interface Hay {
  text: string;
  numbers: Set<number>;
  kw: Set<number>;
  poles: Set<number>;
  dn: Set<number>;
}
const hayCache = new WeakMap<LibraryItem, Hay>();

function collect(text: string, re: RegExp, into: Set<number>): void {
  for (const m of text.matchAll(re)) into.add(Number(m[1] ?? m[0]));
}

/** Everything a typed word can match for one object: its name, its category, its data. */
function hayOf(item: LibraryItem): Hay {
  const hit = hayCache.get(item);
  if (hit) return hit;
  const f = facetsOf(item);
  const parts = [item.name, item.category];
  const kw = new Set<number>();
  const poles = new Set<number>();
  const dn = new Set<number>();
  const extra = new Set<number>();
  if (f.pump) {
    const p = f.pump;
    parts.push(p.series, p.model, p.view, `dn${p.inletDn}`, `dn${p.outletDn}`);
    dn.add(p.inletDn);
    dn.add(p.outletDn);
    if (p.kW !== null) {
      parts.push(`${p.kW} kw`);
      kw.add(p.kW);
      extra.add(p.kW);
    }
    if (p.poles !== null) {
      parts.push(`${p.poles} pole`);
      poles.add(p.poles);
      extra.add(p.poles);
    }
    extra.add(p.inletDn);
    extra.add(p.outletDn);
  }
  if (f.fitting) {
    for (const v of valuesOf(f, "fittingSize")) {
      parts.push(`dn${v}`);
      dn.add(v as number);
      extra.add(v as number);
    }
  }
  const text = parts.join(" ").toLowerCase();
  const numbers = new Set<number>(extra);
  collect(text, /\d+(?:\.\d+)?/g, numbers);
  collect(text, /(\d+(?:\.\d+)?)\s*kw\b/g, kw);
  collect(text, /(\d+)\s*-?\s*poles?\b/g, poles);
  collect(text, /\bdn\s*(\d+)/g, dn);
  const out = { text, numbers, kw, poles, dn };
  hayCache.set(item, out);
  return out;
}

function matchesTerm(h: Hay, t: Term): boolean {
  switch (t.t) {
    case "text":
      return h.text.includes(t.s);
    case "num":
      return h.numbers.has(t.n);
    case "kw":
      return h.kw.has(t.n);
    case "poles":
      return h.poles.has(t.n);
    case "dn":
      return h.dn.has(t.n);
  }
}

/** Does this object match every word? An empty query matches everything. */
export function matchesWords(item: LibraryItem, terms: readonly Term[]): boolean {
  if (terms.length === 0) return true;
  const h = hayOf(item);
  return terms.every((t) => matchesTerm(h, t));
}

export type MatchMode = "all" | "phrase" | "words";
export interface Matcher {
  /** all = nothing typed; phrase = the exact text is in some name; words = every word, in any order. */
  mode: MatchMode;
  (item: LibraryItem): boolean;
}

/**
 * The matcher for the text typed, made for a list of objects.
 * 1. The exact text (lower case, spaces made single) is looked for in the names, categories and data
 *    of the list. When any object has it, only the objects that have it match. This is how the panel
 *    always found things, so "reduces to 50" still gives the tees that reduce to 50 and nothing else.
 * 2. When no object has the exact text, an object matches when it matches EVERY word, in any order
 *    (parseQuery): "cm 55 kw plan", "plan 55kw CM", "2 pole", "dn 100".
 * A text with no letter and no digit matches everything.
 */
export function makeMatcher(rows: readonly FilterRow[], text: string): Matcher {
  const phrase = text.trim().toLowerCase().replace(/\s+/g, " ");
  if (!/[\p{L}\p{N}]/u.test(phrase)) return Object.assign(() => true, { mode: "all" as const });
  if (rows.some((r) => hayOf(r.item).text.includes(phrase))) {
    return Object.assign((item: LibraryItem) => hayOf(item).text.includes(phrase), { mode: "phrase" as const });
  }
  const terms = parseQuery(text);
  return Object.assign((item: LibraryItem) => matchesWords(item, terms), { mode: "words" as const });
}

// ------------------------------------------------------------------------------ the filter --

function passesShow(r: FilterRow, show: ShowMode, favs: ReadonlySet<string>): boolean {
  switch (show) {
    case "all":
      return true;
    case "favourites":
      return favs.has(r.item.id);
    case "mine":
      return r.source !== "builtin";
    case "builtin":
      return r.source === "builtin";
  }
}

/**
 * Do the facet controls accept this object? Every control that is set must match.
 * `only` limits the check to the controls of one kind; `skip` leaves one control out.
 */
function passesFacets(f: Facets, s: FilterState, only?: RowKind, skip?: FacetKey): boolean {
  for (const key of FACET_KEYS) {
    const want = s[key];
    if (want === null || key === skip) continue;
    if (only && FACET_KIND[key] !== only) continue;
    if (!valuesOf(f, key).includes(want)) return false;
  }
  return true;
}

/** The objects that pass SHOW, WORDS and FACETS together. Order is kept. */
export function filterRows<T extends FilterRow>(rows: readonly T[], s: FilterState, favs: ReadonlySet<string>): T[] {
  if (!isFiltering(s)) return [...rows];
  const match = makeMatcher(rows, s.text);
  return rows.filter((r) => passesShow(r, s.show, favs) && match(r.item) && passesFacets(facetsOf(r.item), s));
}

// ------------------------------------------------------------------------------ the options --

export interface Opt<V> {
  value: V;
  /** How many objects the control would leave, with every other control as it is. */
  count: number;
}
export type FacetOptions = { [K in FacetKey]: Array<Opt<K extends "series" | "view" | "fittingKind" ? string : number>> };

const FITTING_ORDER = ["bend90", "bend45", "bendup", "benddown", "tee", "teeup", "teedown", "reducer", "cap", "lintpot", "checkvalve", "butterfly"];
const byValue = (a: Opt<string | number>, b: Opt<string | number>): number =>
  typeof a.value === "number" && typeof b.value === "number" ? a.value - b.value : String(a.value).localeCompare(String(b.value));

/**
 * The values each control offers, taken from the data, with a count for each.
 * The LIST of values is every value that any object has, so a control does not change shape while the
 * person works. The COUNT of a value is the number of objects that pass SHOW, WORDS and the other
 * controls of the SAME KIND with that value: a pump control never promises a pump that is filtered out,
 * and a pump control does not empty a fitting control. A count of 0 means "no result with this value".
 * A value that is set stays in its list.
 */
export function facetOptions(rows: readonly FilterRow[], s: FilterState, favs: ReadonlySet<string>): FacetOptions {
  const match = makeMatcher(rows, s.text);
  const tally = new Map<FacetKey, Map<string | number, number>>(FACET_KEYS.map((k) => [k, new Map()]));
  for (const r of rows) {
    const f = facetsOf(r.item);
    const kind: RowKind = f.pump ? "pump" : f.fitting ? "fitting" : "other";
    if (kind === "other") continue;
    const open = passesShow(r, s.show, favs) && match(r.item);
    for (const key of FACET_KEYS) {
      if (FACET_KIND[key] !== kind) continue;
      const m = tally.get(key)!;
      const counts = open && passesFacets(f, s, kind, key);
      for (const v of new Set(valuesOf(f, key))) m.set(v, (m.get(v) ?? 0) + (counts ? 1 : 0));
    }
  }
  const out = {} as Record<FacetKey, Array<Opt<string | number>>>;
  for (const key of FACET_KEYS) {
    const m = tally.get(key)!;
    const want = s[key];
    if (want !== null && !m.has(want)) m.set(want, 0);
    const list = [...m.entries()].map(([value, count]) => ({ value, count }));
    if (key === "fittingKind") {
      const rank = (v: string | number) => {
        const i = FITTING_ORDER.indexOf(String(v));
        return i < 0 ? FITTING_ORDER.length : i;
      };
      list.sort((a, b) => rank(a.value) - rank(b.value) || byValue(a, b));
    } else {
      list.sort(byValue);
    }
    out[key] = list;
  }
  return out as FacetOptions;
}

/** How many objects of each kind are in the list. The controls of a kind show only when its count is above 0. */
export function kindsPresent(rows: readonly FilterRow[]): Record<RowKind, number> {
  const c: Record<RowKind, number> = { pump: 0, fitting: 0, other: 0 };
  for (const r of rows) c[kindOf(r.item)]++;
  return c;
}

// -------------------------------------------------------------------------------- the counts --

export interface Summary {
  pump: { shown: number; total: number };
  fitting: { shown: number; total: number };
  other: { shown: number; total: number };
  /** Favourites that have an object in the list (an id with no object is not counted). */
  favourites: number;
  shown: number;
  total: number;
}

export function summarise(all: readonly FilterRow[], shown: readonly FilterRow[], favs: ReadonlySet<string>): Summary {
  const sum: Summary = {
    pump: { shown: 0, total: 0 },
    fitting: { shown: 0, total: 0 },
    other: { shown: 0, total: 0 },
    favourites: 0,
    shown: shown.length,
    total: all.length
  };
  const starred = new Set<string>();
  for (const r of all) {
    sum[kindOf(r.item)].total++;
    if (favs.has(r.item.id)) starred.add(r.item.id);
  }
  for (const r of shown) sum[kindOf(r.item)].shown++;
  sum.favourites = starred.size;
  return sum;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** The count line, for example "37 of 272 pumps, 65 of 65 fittings, 12 favourites". */
export function describeCounts(sum: Summary, filtering: boolean): string {
  const parts: string[] = [];
  const add = (c: { shown: number; total: number }, one: string, many: string) => {
    if (c.total === 0) return;
    const noun = plural(c.total, one, many);
    parts.push(filtering ? `${c.shown} of ${c.total} ${noun}` : `${c.total} ${noun}`);
  };
  add(sum.pump, "pump", "pumps");
  add(sum.fitting, "pipe fitting", "pipe fittings");
  add(sum.other, "other object", "other objects");
  if (parts.length === 0) parts.push("No objects");
  parts.push(`${sum.favourites} ${plural(sum.favourites, "favourite", "favourites")}`);
  return parts.join(", ");
}

// --------------------------------------------------------------------------------- the labels --

export const SHOW_LABEL: Record<ShowMode, string> = {
  all: "All",
  favourites: "Favourites",
  mine: "My objects",
  builtin: "Built-in"
};

const FITTING_LABEL: Record<string, string> = {
  bend90: "90° bend",
  bend45: "45° bend",
  bendup: "90° bend, up",
  benddown: "90° bend, down",
  cap: "End cap",
  tee: "Tee",
  teeup: "Tee, branch up",
  teedown: "Tee, branch down",
  reducer: "Reducer",
  lintpot: "Lint pot",
  checkvalve: "Check valve",
  butterfly: "Butterfly valve"
};
export const fittingKindLabel = (kind: string): string => FITTING_LABEL[kind] ?? kind;

export const viewLabel = (view: string): string => (view ? view.charAt(0).toUpperCase() + view.slice(1) : view);
